// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * FuelMonitor - Monitors fuel levels and triggers voice announcements
 * 
 * Watches fuel percentage and plays appropriate voice warnings:
 * - low_fuel_warning: < 30%
 * - fuel_critical: < 10%
 * - fuel_depleted: = 0%
 * - refueling_complete: When refueling finishes
 */

import EventBus, { EVENT_TYPES } from '../system/EventBus';

class FuelMonitorClass {
  constructor() {
    this.lastFuelPercent = 100;
    this.hasPlayedLowFuel = false;
    this.hasPlayedCritical = false;
    this.hasPlayedDepleted = false;
    this.wasRefueling = false;
    
    // Thresholds
    this.LOW_FUEL_THRESHOLD = 30;
    this.CRITICAL_THRESHOLD = 10;
    this.DEPLETED_THRESHOLD = 0;
    this.REFUEL_COMPLETE_THRESHOLD = 95; // Consider refueling complete at 95%+
  }

  /**
   * Update fuel monitoring
   * Call this whenever fuel changes
   * @param {number} fuelPercent - Current fuel percentage (0-100)
   */
  update(fuelPercent) {
    // Detect refueling (fuel increasing significantly)
    const isRefueling = fuelPercent > this.lastFuelPercent + 5; // 5% increase = refueling
    
    // Refueling complete detection
    if (this.wasRefueling && !isRefueling && fuelPercent >= this.REFUEL_COMPLETE_THRESHOLD) {
      EventBus.emit(EVENT_TYPES.REFUELING_COMPLETE);
      this.resetWarnings(); // Reset all warnings after refueling
    }
    
    this.wasRefueling = isRefueling;
    
    // Don't play warnings while refueling
    if (isRefueling) {
      this.lastFuelPercent = fuelPercent;
      return;
    }
    
    // Fuel depleted (0%)
    if (fuelPercent <= this.DEPLETED_THRESHOLD && !this.hasPlayedDepleted) {
      EventBus.emit(EVENT_TYPES.FUEL_DEPLETED);
      this.hasPlayedDepleted = true;
      this.lastFuelPercent = fuelPercent;
      return;
    }
    
    // Fuel critical (< 10%)
    if (fuelPercent < this.CRITICAL_THRESHOLD && fuelPercent > this.DEPLETED_THRESHOLD && !this.hasPlayedCritical) {
      EventBus.emit(EVENT_TYPES.FUEL_CRITICAL);
      this.hasPlayedCritical = true;
      this.lastFuelPercent = fuelPercent;
      return;
    }
    
    // Low fuel warning (< 30%)
    if (fuelPercent < this.LOW_FUEL_THRESHOLD && fuelPercent >= this.CRITICAL_THRESHOLD && !this.hasPlayedLowFuel) {
      EventBus.emit(EVENT_TYPES.LOW_FUEL_WARNING);
      this.hasPlayedLowFuel = true;
      this.lastFuelPercent = fuelPercent;
      return;
    }
    
    // Reset warnings if fuel goes back above thresholds
    if (fuelPercent >= this.LOW_FUEL_THRESHOLD) {
      this.resetWarnings();
    } else if (fuelPercent >= this.CRITICAL_THRESHOLD) {
      // Reset depleted warning if fuel goes above critical
      this.hasPlayedDepleted = false;
    } else if (fuelPercent > this.DEPLETED_THRESHOLD) {
      // Reset depleted warning if fuel goes above 0
      this.hasPlayedDepleted = false;
    }
    
    this.lastFuelPercent = fuelPercent;
  }

  /**
   * Reset all warning flags
   * Called after refueling or when fuel goes above low threshold
   */
  resetWarnings() {
    this.hasPlayedLowFuel = false;
    this.hasPlayedCritical = false;
    this.hasPlayedDepleted = false;
  }

  /**
   * Manually trigger refueling complete
   * Useful for instant refueling (like at stations)
   */
  triggerRefuelingComplete() {
    EventBus.emit(EVENT_TYPES.REFUELING_COMPLETE);
    this.resetWarnings();
  }
}

// Create singleton instance
const FuelMonitor = new FuelMonitorClass();

export default FuelMonitor;
